import React, { useState } from 'react';
import { DeviceCreate } from '../../api/models';
import { useI18n } from '../../contexts/I18nContext';
import { DeviceDetailModal } from './DeviceDetailModal';
import { useDevicesList } from './devicesList/useDevicesList';
import { useDevicesExport } from './devicesList/useDevicesExport';
import { DevicesFilters } from './devicesList/DevicesFilters';
import { DevicesPagination } from './devicesList/DevicesPagination';
import { DeviceSortField } from './devicesList/deviceQuery';

interface DevicesTableProps {
  onDeviceSelect?: (device: DeviceCreate) => void;
  refreshTrigger?: number;
  initialDeviceTypeFilter?: string;
  /** 'active' or 'refresh': the status filter to start with (dashboard links). */
  initialStatusFilter?: string;
  /** Client ids to start filtered on (dashboard links). */
  initialClientFilter?: string[];
}

/**
 * The device list: filters on top, one row per device, a pager, and the
 * device dialog on click. The data and the filters live in
 * `useDevicesList`, the Excel export in `useDevicesExport`.
 */
export const DevicesTable: React.FC<DevicesTableProps> = ({ onDeviceSelect, refreshTrigger, initialDeviceTypeFilter, initialStatusFilter, initialClientFilter }) => {
  const { formatDate, t } = useI18n();
  const list = useDevicesList({ refreshTrigger, initialDeviceTypeFilter, initialStatusFilter, initialClientFilter });
  const exporter = useDevicesExport(list.filters, (clientId) => {
    const name = list.clientName(clientId);
    return name === '-' ? clientId : name;
  });
  const [selectedDevice, setSelectedDevice] = useState<DeviceCreate | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const openDevice = (device: DeviceCreate) => {
    setSelectedDevice(device);
    setIsModalOpen(true);
    onDeviceSelect?.(device);
  };

  const sortIcon = (field: DeviceSortField) => (field !== list.sortField ? '↕️' : list.sortDirection === 'asc' ? '↑' : '↓');

  if (list.loading) {
    return (
      <div className="devices-table-loading">
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Loading devices...</p>
        </div>
      </div>
    );
  }

  if (list.error) {
    return (
      <div className="devices-table-error">
        <div className="error-container">
          <h3>{t('devices.errorLoading')}</h3>
          <p>{list.error}</p>
          <button type="button" onClick={() => window.location.reload()}>{t('errors.retryAction')}</button>
        </div>
      </div>
    );
  }

  return (
    <div className="devices-table-container">
      <DevicesFilters
        list={list}
        exportControl={{ canExport: exporter.canExport, exporting: exporter.exporting, notice: exporter.notice, onExport: exporter.exportExcel }}
      />

      <div className="table-wrapper">
        <table className="devices-table">
          <thead>
            <tr>
              <th onClick={() => list.sort('idDevice')} className="sortable">{t('devices.deviceId')} {sortIcon('idDevice')}</th>
              <th onClick={() => list.sort('deviceType')} className="sortable">{t('devices.deviceType')} {sortIcon('deviceType')}</th>
              <th className="sortable">{t('common.model')}</th>
              <th onClick={() => list.sort('dateTime')} className="sortable">{t('devices.lastUpdated')} {sortIcon('dateTime')}</th>
              <th>{t('devices.client')}</th>
              <th onClick={() => list.sort('needsRefresh')} className="sortable">{t('devices.status')} {sortIcon('needsRefresh')}</th>
            </tr>
          </thead>
          <tbody>
            {list.pageRows.map((device: any) => (
              <tr key={device.idDevice} onClick={() => openDevice(device)} className="device-row">
                <td className="device-id">{device.idDevice}</td>
                <td className="device-type">{device.deviceType || 'N/A'}</td>
                <td className="device-model">{device.model || device.model_ref || '-'}</td>
                <td className="date-time">{formatDate(device.dateTime)}</td>
                <td className="device-client">{list.clientName(device.idClient)}</td>
                <td className="status">
                  <span className={`status-badge ${device.needsRefresh ? 'needs-refresh' : 'active'}`}>
                    {device.needsRefresh ? t('devices.needsRefresh') : t('common.active')}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {list.pageRows.length === 0 && (
          <div className="no-devices">
            <p>No devices found matching your search criteria.</p>
          </div>
        )}
      </div>

      <DevicesPagination
        page={list.currentPage}
        totalPages={list.totalPages}
        totalItems={list.totalItems}
        pageSize={list.itemsPerPage}
        onPageChange={list.setCurrentPage}
        onPageSizeChange={list.changePageSize}
      />

      <DeviceDetailModal
        device={selectedDevice}
        isOpen={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
          setSelectedDevice(null);
        }}
        onDeviceUpdated={(updated) => {
          list.replaceDevice(updated);
          setSelectedDevice(updated);
        }}
      />
    </div>
  );
};
